"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, Badge, Section, Button } from "@/src/components";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/src/components/input/command";
import { Calculator, Calendar, CreditCard, Settings, Smile, User } from "lucide-react";
import { InstallCommand } from "@/components/docs/install-command";

export default function DocsComponentsCommand() {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  return (
    <>
      {/* Page Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Command
            </Text>
            <Badge variant="secondary">Input</Badge>
          </div>
          <Text as="p" className="text-lg text-muted-foreground max-w-2xl">
            A command palette for quick actions and navigation. Built on cmdk with
            keyboard-first design for power users.
          </Text>
        </div>
      </Section>

      {/* Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Add this component to your project</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <InstallCommand command="npx shadcn@latest add @bluecopa-ui/command" />
        </SectionCard>
      </Section>

      {/* Basic Usage */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Basic Command</CardTitle>
              <CardDescription>A simple command palette with search</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <Command className="rounded-lg border shadow-md max-w-md">
            <CommandInput placeholder="Type a command or search..." />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup heading="Suggestions">
                <CommandItem>
                  <Calendar className="mr-2 h-4 w-4" />
                  <span>Calendar</span>
                </CommandItem>
                <CommandItem>
                  <Smile className="mr-2 h-4 w-4" />
                  <span>Search Emoji</span>
                </CommandItem>
                <CommandItem>
                  <Calculator className="mr-2 h-4 w-4" />
                  <span>Calculator</span>
                </CommandItem>
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup heading="Settings">
                <CommandItem>
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                  <CommandShortcut>⌘P</CommandShortcut>
                </CommandItem>
                <CommandItem>
                  <CreditCard className="mr-2 h-4 w-4" />
                  <span>Billing</span>
                  <CommandShortcut>⌘B</CommandShortcut>
                </CommandItem>
                <CommandItem>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                  <CommandShortcut>⌘S</CommandShortcut>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code className="block">{`<Command>`}</code>
            <code className="block">{`  <CommandInput placeholder="Search..." />`}</code>
            <code className="block">{`  <CommandList>`}</code>
            <code className="block">{`    <CommandGroup heading="Actions">`}</code>
            <code className="block">{`      <CommandItem>Action 1</CommandItem>`}</code>
            <code className="block">{`    </CommandGroup>`}</code>
            <code className="block">{`  </CommandList>`}</code>
            <code className="block">{`</Command>`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Command Dialog */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Command Dialog</CardTitle>
              <CardDescription>Open with keyboard shortcut (⌘K)</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="flex items-center gap-4">
            <Button onClick={() => setOpen(true)}>Open Command Palette</Button>
            <Text as="p" className="text-sm text-muted-foreground">
              or press{" "}
              <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                <span className="text-xs">⌘</span>K
              </kbd>
            </Text>
          </div>
          <CommandDialog open={open} onOpenChange={setOpen}>
            <CommandInput placeholder="Type a command or search..." />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup heading="Suggestions">
                <CommandItem>
                  <Calendar className="mr-2 h-4 w-4" />
                  <span>Calendar</span>
                </CommandItem>
                <CommandItem>
                  <Smile className="mr-2 h-4 w-4" />
                  <span>Search Emoji</span>
                </CommandItem>
                <CommandItem>
                  <Calculator className="mr-2 h-4 w-4" />
                  <span>Calculator</span>
                </CommandItem>
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup heading="Settings">
                <CommandItem>
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                  <CommandShortcut>⌘P</CommandShortcut>
                </CommandItem>
                <CommandItem>
                  <CreditCard className="mr-2 h-4 w-4" />
                  <span>Billing</span>
                  <CommandShortcut>⌘B</CommandShortcut>
                </CommandItem>
                <CommandItem>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                  <CommandShortcut>⌘S</CommandShortcut>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </CommandDialog>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code className="block">{`const [open, setOpen] = useState(false);`}</code>
            <code className="block mt-2">{`// Listen for keyboard shortcut`}</code>
            <code className="block">{`useEffect(() => {`}</code>
            <code className="block">{`  const down = (e: KeyboardEvent) => {`}</code>
            <code className="block">{`    if (e.key === "k" && (e.metaKey || e.ctrlKey)) {`}</code>
            <code className="block">{`      e.preventDefault();`}</code>
            <code className="block">{`      setOpen((open) => !open);`}</code>
            <code className="block">{`    }`}</code>
            <code className="block">{`  };`}</code>
            <code className="block">{`  document.addEventListener("keydown", down);`}</code>
            <code className="block">{`  return () => document.removeEventListener("keydown", down);`}</code>
            <code className="block">{`}, []);`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Import */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Import</CardTitle>
              <CardDescription>How to import the component</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code className="block">{`import {`}</code>
            <code className="block">{`  Command,`}</code>
            <code className="block">{`  CommandDialog,`}</code>
            <code className="block">{`  CommandInput,`}</code>
            <code className="block">{`  CommandList,`}</code>
            <code className="block">{`  CommandEmpty,`}</code>
            <code className="block">{`  CommandGroup,`}</code>
            <code className="block">{`  CommandItem,`}</code>
            <code className="block">{`  CommandShortcut,`}</code>
            <code className="block">{`  CommandSeparator,`}</code>
            <code className="block">{`} from "@/registry/new-york/ui/command";`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Components */}
      <Section>
        <SectionCard
          header={<CardTitle>Components</CardTitle>}
          contentClassName="p-6"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-semibold">Component</th>
                <th className="text-left py-2 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">Command</td>
                <td className="py-2 text-muted-foreground">Root container for the command palette</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">CommandDialog</td>
                <td className="py-2 text-muted-foreground">Modal dialog wrapper (⌘K style)</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">CommandInput</td>
                <td className="py-2 text-muted-foreground">Search input field</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">CommandList</td>
                <td className="py-2 text-muted-foreground">Scrollable list container</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">CommandEmpty</td>
                <td className="py-2 text-muted-foreground">Empty state when no results</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">CommandGroup</td>
                <td className="py-2 text-muted-foreground">Group of related items with heading</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">CommandItem</td>
                <td className="py-2 text-muted-foreground">Individual selectable item</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">CommandShortcut</td>
                <td className="py-2 text-muted-foreground">Keyboard shortcut hint</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">CommandSeparator</td>
                <td className="py-2 text-muted-foreground">Visual separator between groups</td>
              </tr>
            </tbody>
          </table>
        </SectionCard>
      </Section>
    </>
  );
}
